import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { consoleApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { formatDate } from '../lib/format';
import { assertCurrentSessionGeneration, isCurrentSessionGeneration, onSessionExpired, sessionGeneration } from '../lib/session-events';
import type { ApolloCredentialMetadata } from '../types';
import { ConfirmAction } from './ConfirmAction';

type Notice = 'idle' | 'saved' | 'removed' | 'invalid' | 'refresh-required';

export function credentialEditBlock(metadata: ApolloCredentialMetadata | undefined, canManage: boolean): string | null {
  if (!canManage) return 'Your session is read-only. An administrator can manage the Apollo API key.';
  if (!metadata || metadata.source === null || metadata.revision === null) return 'Load the current key status before saving or removing a key.';
  if (metadata.source === 'FILE') return 'A deployment-managed key is configured. It cannot be viewed or changed here; an administrator must update its server-side secret.';
  if (!metadata.encryption_ready) return 'Encrypted storage is not ready. An administrator must check the service configuration before you submit a key.';
  if (!metadata.editable) return metadata.reason_code === 'apollo_credential_busy' ? 'An Apollo lookup is still active. Wait for it to finish, then refresh key status.' : metadata.reason_code === 'apollo_credential_disabled_required' ? 'Disable the Apollo pilot before changing its key. An administrator must update the service configuration.' : 'Key changes are unavailable. Refresh the status or ask an administrator to check the configuration.';
  return null;
}

export function ApolloCredentials() {
  const { csrfToken, capabilities } = useAuth(), client = useQueryClient();
  const query = useQuery({ queryKey: ['apollo-credentials'], queryFn: consoleApi.apolloCredentials, retry: false, staleTime: 0 });
  const input = useRef<HTMLInputElement | null>(null), mounted = useRef(true), inFlight = useRef(false);
  const bindInput = useCallback((element: HTMLInputElement | null) => { if (!element && input.current) input.current.value = ''; input.current = element; }, []);
  const [hasInput, setHasInput] = useState(false), [busy, setBusy] = useState(false), [notice, setNotice] = useState<Notice>('idle');
  const metadata = query.isError || query.data?.source === null || query.data?.revision === null ? undefined : query.data;
  const blocked = credentialEditBlock(metadata, capabilities.manageProspects);
  const needsRefresh = notice === 'refresh-required';
  const disabled = !!blocked || busy || query.isFetching || needsRefresh;
  const clear = () => { if (input.current) input.current.value = ''; setHasInput(false); };

  useEffect(() => {
    mounted.current = true;
    const unsubscribe = onSessionExpired(() => { if (input.current) input.current.value = ''; });
    return () => { mounted.current = false; unsubscribe(); };
  }, []);
  useEffect(() => { clear(); setNotice('idle'); }, [csrfToken]);

  async function complete(metadata: ApolloCredentialMetadata, generation: number, next: Notice) {
    assertCurrentSessionGeneration(generation);
    if (!mounted.current) return;
    if (metadata.source === null || metadata.revision === null) throw new Error('Key status is unavailable.');
    client.setQueryData(['apollo-credentials'], metadata);
    setNotice(next);
    await Promise.all([client.invalidateQueries({ queryKey: ['apollo-credentials'] }), client.invalidateQueries({ queryKey: ['apollo-budget'] })]);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (disabled || inFlight.current || metadata?.revision == null || !input.current) return;
    let apiKey = input.current.value.trim();
    clear();
    if (apiKey.length < 16 || apiKey.length > 4096 || /\s/.test(apiKey)) { apiKey = ''; setNotice('invalid'); return; }
    const generation = sessionGeneration();
    inFlight.current = true; setBusy(true); setNotice('idle');
    try {
      // Only the in-flight request owns the key; no React state, query key,
      // mutation variables, cache entry or browser storage receives it.
      const request = consoleApi.saveApolloCredentials(apiKey, metadata.revision, csrfToken);
      apiKey = '';
      await complete(await request, generation, 'saved');
    } catch {
      apiKey = '';
      if (mounted.current && isCurrentSessionGeneration(generation)) setNotice('refresh-required');
    } finally {
      inFlight.current = false;
      if (mounted.current && isCurrentSessionGeneration(generation)) setBusy(false);
    }
  }

  async function remove() {
    if (disabled || inFlight.current || metadata?.source !== 'CONSOLE' || !metadata.stored || metadata.revision === null) throw new Error('Refresh key status before continuing.');
    clear();
    const generation = sessionGeneration();
    inFlight.current = true; setBusy(true); setNotice('idle');
    try { await complete(await consoleApi.removeApolloCredentials(metadata.revision, csrfToken), generation, 'removed'); }
    catch {
      if (mounted.current && isCurrentSessionGeneration(generation)) setNotice('refresh-required');
      throw new Error('Removal could not be confirmed. Refresh key status.');
    } finally {
      inFlight.current = false;
      if (mounted.current && isCurrentSessionGeneration(generation)) setBusy(false);
    }
  }

  async function refresh() {
    clear();
    const generation = sessionGeneration(), result = await query.refetch();
    if (mounted.current && isCurrentSessionGeneration(generation) && !result.isError) setNotice('idle');
  }

  return <section className="apollo-credentials" aria-labelledby="apollo-credentials-title">
    <h3 id="apollo-credentials-title">Apollo API key</h3>
    <p className="list-note">{query.isLoading ? 'Loading key status…' : !metadata ? 'Key status unavailable.' : metadata.source === 'FILE' ? 'Managed by deployment.' : metadata.stored ? metadata.usable ? 'Saved and encrypted in GTM.' : 'A key is saved, but is not currently usable by GTM.' : 'No key saved in GTM.'} {metadata?.updated_at ? <>Last changed {formatDate(metadata.updated_at)}.</> : null} Existing keys are never displayed.</p>
    <p className="list-note">Saving does not check Apollo access or pricing, enable the pilot, spend credits or send email.</p>
    {blocked ? <p className="list-note">{blocked}</p> : null}
    {metadata?.source !== 'FILE' && capabilities.manageProspects ? <form className="apollo-credentials__form" autoComplete="off" onSubmit={event => void save(event)}>
      <label htmlFor="apollo-api-key"><span>{metadata?.stored ? 'Replacement API key' : 'API key'}</span>
        <input ref={bindInput} id="apollo-api-key" type="password" autoComplete="new-password" autoCapitalize="none" spellCheck={false} minLength={16} maxLength={4096} required disabled={disabled}
          aria-describedby="apollo-key-storage" onInput={event => setHasInput(event.currentTarget.value.length > 0)} />
      </label>
      <p id="apollo-key-storage" className="list-note">Paste your key here. It is encrypted on the server and cleared from this field when submitted or cancelled.</p>
      <div className="action-row">
        <button type="submit" className="button" disabled={disabled || !hasInput}>{busy ? 'Saving…' : metadata?.stored ? 'Replace API key' : 'Save API key'}</button>
        <button type="button" className="button button--quiet" disabled={busy || !hasInput} onClick={clear}>Cancel entry</button>
      </div>
    </form> : null}
    {query.isError ? <p role="alert" className="inline-alert inline-alert--error">Key status could not load. Refresh it before entering a key. No key was submitted by this check.</p> : null}
    {notice === 'invalid' ? <p role="alert" className="inline-alert inline-alert--error">Use the Apollo API key only, with 16–4096 characters and no whitespace. The field has been cleared.</p> : null}
    {notice === 'refresh-required' ? <p role="alert" className="inline-alert inline-alert--error">The change could not be confirmed. The key field is cleared and no automatic retry was made. Refresh key status before entering it again.</p> : null}
    {notice === 'saved' || notice === 'removed' ? <p role="status" className="inline-alert inline-alert--success">{notice === 'saved' ? 'Key saved securely. Apollo access is not yet verified; pilot settings and credit limits are unchanged.' : 'Saved GTM key removed. This does not revoke the key in Apollo or change pilot settings.'}</p> : null}
    <div className="action-row apollo-credentials__management">
      <button type="button" className="button button--quiet" disabled={busy || query.isFetching} onClick={() => void refresh()}>{query.isFetching ? 'Checking key status…' : 'Refresh key status'}</button>
      {metadata?.source === 'CONSOLE' && metadata.stored && capabilities.manageProspects ? <ConfirmAction buttonLabel="Remove saved key" confirmLabel="Remove saved key" danger disabled={disabled} onConfirm={remove}>
        <p>Remove the encrypted Apollo key saved in GTM. The key is not displayed, and this does not revoke it in your Apollo account.</p>
        <p>Future Apollo lookups will remain unavailable until a usable key is configured. Existing research and staged results are retained.</p>
      </ConfirmAction> : null}
    </div>
  </section>;
}
